import { ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, Empty, Spin, Tag, Tooltip, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  EstimateActivity,
  GSTRetrofit,
  ZohoReconcileResult,
  fetchEstimateActivity,
  reconcileEstimateZoho,
  retryGSTRetrofit,
} from "../../../apis/gst_retrofit.api";

interface Props {
  open: boolean;
  onClose: () => void;
  estimateId?: number | string;
  leadId?: string;
}

const statusColor: Record<string, string> = {
  pending: "gold",
  processing: "blue",
  completed: "green",
  failed: "red",
};

// `po_statuses` is keyed by whatever engine ran the cascade: a legacy PO id, or `oms:<sales order>`
// (`oms` alone when the estimate is OMS-managed but was never placed). The per-document detail for an
// OMS conversion lives on the OMS order page, so that key is rendered as a link to it.
const targetLabel = (key: string): { label: string; salesOrderId?: number } => {
  if (key === "oms") return { label: "OMS · not placed" };
  if (key.startsWith("oms:")) {
    const id = Number(key.slice(4));
    return Number.isFinite(id) && id > 0
      ? { label: `OMS SO #${id}`, salesOrderId: id }
      : { label: "OMS" };
  }
  return { label: `PO ${key}` };
};

const formatTs = (iso: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const GSTRetrofitSidebar: React.FC<Props> = ({ open, onClose, estimateId, leadId }) => {
  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState<EstimateActivity | null>(null);
  const [reconcile, setReconcile] = useState<ZohoReconcileResult | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const queryParams = useMemo(() => {
    if (estimateId) return { estimate_id: estimateId };
    if (leadId) return { lead_id: leadId };
    return null;
  }, [estimateId, leadId]);

  const loadActivity = useCallback(async () => {
    if (!queryParams) return;
    setLoading(true);
    try {
      const data = await fetchEstimateActivity(queryParams);
      setActivity(data);
    } catch (e: any) {
      message.error(e?.response?.data?.error || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const doReconcile = useCallback(async () => {
    if (!queryParams) return;
    setReconcileLoading(true);
    try {
      const r = await reconcileEstimateZoho(queryParams);
      setReconcile(r);
    } catch (e: any) {
      message.error(e?.response?.data?.error || "Reconcile failed");
    } finally {
      setReconcileLoading(false);
    }
  }, [queryParams]);

  const doRetry = useCallback(
    async (retrofitId: number) => {
      setRetryingId(retrofitId);
      try {
        const res = await retryGSTRetrofit(retrofitId);
        message.success(
          res.cascade_queued ? "Retry queued in Zoho" : "Retry marked PENDING (queue unavailable)"
        );
        await loadActivity();
      } catch (e: any) {
        message.error(e?.response?.data?.error || "Retry failed");
      } finally {
        setRetryingId(null);
      }
    },
    [loadActivity]
  );

  useEffect(() => {
    if (open && queryParams) {
      loadActivity();
    }
  }, [open, queryParams, loadActivity]);

  const retrofits = useMemo<GSTRetrofit[]>(() => {
    if (!activity) return [];
    return [...activity.retrofits].sort((a, b) =>
      (b.created_at || "").localeCompare(a.created_at || "")
    );
  }, [activity]);

  const hasActionable = useMemo(
    () => retrofits.some((r) => r.status === "failed" || r.status === "pending"),
    [retrofits]
  );

  const renderReconcileBanner = () => {
    if (reconcileLoading) return <Spin size="small" />;
    if (!reconcile) return null;

    if (reconcile.in_sync === true) {
      return null;
    }
    if (reconcile.in_sync === false) {
      return (
        <Alert
          type="error"
          showIcon
          message="DB and Zoho diverge"
          description={
            <div style={{ fontSize: 12 }}>
              <div>DB GSTIN: {reconcile.estimate_gst || "—"}</div>
              <div>Zoho GSTIN: {reconcile.zoho_gst || "—"}</div>
              {reconcile.zoho_contact_name && <div>Contact: {reconcile.zoho_contact_name}</div>}
            </div>
          }
        />
      );
    }
    return (
      <Alert
        type="warning"
        showIcon
        message="Could not verify Zoho sync"
        description={reconcile.reason || reconcile.error || "Unknown"}
      />
    );
  };

  const renderRetrofit = (r: GSTRetrofit) => {
    const poStatuses = Object.entries(r.po_statuses || {});
    const retryable = r.status === "failed" || r.status === "pending";
    return (
      <div
        key={`r-${r.id}`}
        style={{ borderLeft: `3px solid ${statusColor[r.status] || "gray"}`, padding: "8px 12px", marginBottom: 8 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Tag color={statusColor[r.status]}>{r.status.toUpperCase()}</Tag>
            <strong>GST retrofit #{r.id}</strong>
          </div>
          <span style={{ fontSize: 11, color: "#888" }}>{formatTs(r.created_at)}</span>
        </div>
        <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
          By {r.initiated_by?.name || "—"} · address {r.old_bill_address_id || "—"} → {r.new_bill_address_id || "—"}
        </div>
        {poStatuses.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 12 }}>
            {poStatuses.map(([key, s]) => {
              const { label, salesOrderId } = targetLabel(key);
              const tag = (
                <Tag
                  key={key}
                  color={s === "completed" ? "green" : s === "failed" ? "red" : "blue"}
                  style={salesOrderId ? { cursor: "pointer" } : undefined}
                  onClick={
                    salesOrderId
                      ? () => window.open(`/oms/${salesOrderId}`, "_blank", "noopener,noreferrer")
                      : undefined
                  }
                >
                  {label}: {s}
                </Tag>
              );
              return salesOrderId ? (
                <Tooltip key={key} title="Open the OMS order to see each Zoho document it re-billed">
                  {tag}
                </Tooltip>
              ) : (
                tag
              );
            })}
          </div>
        )}
        {r.error_message && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#cf1322" }}>
            <Tooltip title={r.error_message}>
              <span>{r.error_message.slice(0, 200)}{r.error_message.length > 200 ? "…" : ""}</span>
            </Tooltip>
          </div>
        )}
        {retryable && (
          <div style={{ marginTop: 8 }}>
            <Button
              size="small"
              type="primary"
              danger={r.status === "failed"}
              icon={<ReloadOutlined />}
              loading={retryingId === r.id}
              onClick={() => doRetry(r.id)}
            >
              Retry Zoho cascade
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Billing Retrofits</span>
          {hasActionable && (
            <Button size="small" icon={<SyncOutlined />} onClick={doReconcile} loading={reconcileLoading}>
              Reconcile
            </Button>
          )}
        </div>
      }
      placement="right"
      width={460}
      open={open}
      onClose={onClose}
    >
      <div style={{ marginBottom: 12 }}>{renderReconcileBanner()}</div>

      {loading ? (
        <Spin />
      ) : !activity ? (
        <Empty />
      ) : retrofits.length === 0 ? (
        <Empty description="No billing retrofits yet" />
      ) : (
        <div>{retrofits.map((r) => renderRetrofit(r))}</div>
      )}
    </Drawer>
  );
};

export default GSTRetrofitSidebar;
